import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import React, { useEffect, useRef, useState } from 'react';

import { Button, Modal, TextField } from '@thinkific/chalk/react-components';

import type { Editor } from 'components/new/Posts/hooks/useEditor/useEditor';
import ThemeProvider from 'components/new/providers/ThemeProvider';

import * as css from './LinkForm.css';

interface LinkFormProps {
  editor: Editor;
  isOpen: boolean;
  onChange: () => void;
  onClose: () => void;
}

interface linkAttributes {
  text: string;
  url: string;
}

interface formErrors {
  text?: string;
  url?: string;
}

const LinkForm = ({ editor, isOpen, onClose, onChange }: LinkFormProps) => {
  const [link, setLink] = useState<linkAttributes>({
    text: '',
    url: '',
  });

  const isEditingLink = useRef<boolean>(false);

  const [errors, setErrors] = useState<formErrors>({});

  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLink((prevLink) => ({ ...prevLink, [name]: value }));
  };

  const validateForm = () => {
    const validationErrors: formErrors = {};

    if (!link.text.trim()) {
      validationErrors.text = t(
        'components-documentEditor-customLink_invalidText',
        'Text cannot be empty'
      );
    }

    if (!/^https?:\/\//.test(link.url)) {
      validationErrors.url = t(
        'components-documentEditor-customLink_invalidUrl',
        'Please use a valid URL'
      );
    }

    return validationErrors;
  };

  const applyLink = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!editor) return;
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) return;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .command(({ tr, dispatch }) => {
        if (!dispatch) return true;

        const { from, to } = tr.selection;

        const displayText = link.text || link.url || '';

        tr.insertText(displayText, from, to);
        tr.addMark(
          from,
          from + displayText.length,
          editor.schema.marks.link.create({
            href: link.url,
          })
        );

        return true;
      })
      .unsetMark('link')
      .insertContent(' ')
      .focus()
      .run();
    onChange?.();
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().unsetMark('link', { extendEmptyMarkRange: true }).run();
    onChange?.();
  };

  useEffect(() => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;

    const linkNode = editor.state.doc.nodeAt(from);

    if (linkNode?.marks?.[0]?.type?.name === 'link') {
      setLink({
        text: linkNode.textContent,
        url: linkNode.marks[0].attrs.href,
      });
      isEditingLink.current = true;
    } else {
      setLink({
        text: empty ? '' : editor.state.doc.textBetween(from, to, ' '),
        url: editor.getAttributes('link').href || '',
      });
      isEditingLink.current = false;
    }
  }, [editor, isOpen]);

  return (
    <>
      {/* Need to put the modal in a portal since the Bubble menu parent is using transform and doesn't work with position fixed. */}
      {createPortal(
        <ThemeProvider>
          <Modal
            open={isOpen}
            primaryAction={{
              label:
                isEditingLink.current === true
                  ? t('components-documentEditor-customLink_modalAction_edit', 'Update')
                  : t('components-documentEditor-customLink_modalAction_insert', 'Insert'),
              onClick: applyLink,
            }}
            title={
              isEditingLink.current === true
                ? t('components-documentEditor-customLink_modalTitle_edit', 'Edit link')
                : t('components-documentEditor-customLink_modalTitle_insert', 'Insert link')
            }
            onClose={onClose}
          >
            <div className={css.modalBody}>
              <TextField
                error={errors.text && errors.text}
                label={t('components-documentEditor-customLink_linkTextLabel', 'Text')}
                name="text"
                type="text"
                value={link.text}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyLink(e);
                  }
                }}
              />
              <TextField
                error={errors.url}
                label={t('components-documentEditor-customLink_linkUrlLabel', 'URL')}
                name="url"
                placeholder="https://www.example.com"
                type="url"
                value={link.url}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyLink(e);
                  }
                }}
              />
              {isEditingLink.current === true && (
                <Button
                  className={css.buttonContainer}
                  emphasis="medium"
                  size="sm"
                  onClick={removeLink}
                >
                  {t('components-documentEditor-customLink_removeLink', 'Remove link')}
                </Button>
              )}
            </div>
          </Modal>
        </ThemeProvider>,
        document.body
      )}
    </>
  );
};

export default LinkForm;
